import type { Areas, LoadType, SingularStationArea } from '@thive/fddm-types';

import type { Area } from '@/modules/floorplanDesign/types';
import { CAPACITY_EPSILON } from '@/helpers/angleConstants';

export type StationCarrierSide = 'short' | 'long';

type AreaFootprintDimension = 'length' | 'width';
type Stacking = SingularStationArea['constellationSpec']['stacking'];
type StationFootprint = Pick<SingularStationArea['stationSpec'], 'length' | 'width'>;

const MINIMUM_STATIONS_PER_LANE = 1;

const AREA_FIT_DIMENSION_BY_STACKING: Record<Stacking, AreaFootprintDimension> = {
  HORIZONTAL_TOP: 'width',
  HORIZONTAL_MIDDLE: 'width',
  HORIZONTAL_BOTTOM: 'width',
  VERTICAL_LEFT: 'length',
  VERTICAL_MIDDLE: 'length',
  VERTICAL_RIGHT: 'length',
};

/**
 * StationSpec must always have length greater than or equal to width.
 */
export const getStationFootprintFromLoadType = (loadType: LoadType): StationFootprint => ({
  length: Math.max(loadType.length, loadType.width),
  width: Math.min(loadType.length, loadType.width),
});

export const createLoadTypeDimensionsById = (
  loadTypes: readonly LoadType[],
): ReadonlyMap<string, StationFootprint> =>
  new Map(
    loadTypes.map((loadType): [string, StationFootprint] => [
      loadType.id,
      getStationFootprintFromLoadType(loadType),
    ]),
  );

export const getCarrierSideFromPoiAngle = (angle: number): StationCarrierSide => {
  const shortSideAlignment = Math.abs(Math.cos(angle));
  const longSideAlignment = Math.abs(Math.sin(angle));

  if (longSideAlignment > shortSideAlignment) return 'long';

  return 'short';
};

const getCarrierSideFromStationWidth = (
  stationWidth: number,
  loadTypeDimensions: StationFootprint,
): StationCarrierSide => {
  const distanceFromShortSide = Math.abs(stationWidth - loadTypeDimensions.width);
  const distanceFromLongSide = Math.abs(stationWidth - loadTypeDimensions.length);

  if (distanceFromLongSide < distanceFromShortSide) return 'long';

  return 'short';
};

export const getStationCarrierSide = (
  area: SingularStationArea,
  loadType?: LoadType,
): StationCarrierSide => {
  const loadHandlingPoiSpec = area.loadHandlingPoiSpecs[0];

  if (loadHandlingPoiSpec?.alwaysFitStation && loadType) {
    const loadTypeDimensions = getStationFootprintFromLoadType(loadType);

    return getCarrierSideFromStationWidth(area.stationSpec.width, loadTypeDimensions);
  }

  return getCarrierSideFromPoiAngle(loadHandlingPoiSpec?.angle ?? 0);
};

export const getStationWidthForCarrierSide = (
  loadType: LoadType,
  carrierSide: StationCarrierSide,
): number => {
  const loadTypeDimensions = getStationFootprintFromLoadType(loadType);

  if (carrierSide === 'long') return loadTypeDimensions.length;

  return loadTypeDimensions.width;
};

const getAreaFitDimension = (stacking: Stacking): AreaFootprintDimension =>
  AREA_FIT_DIMENSION_BY_STACKING[stacking];

const getStationWidthFromFootprint = (
  loadTypeDimensions: StationFootprint,
  carrierSide: StationCarrierSide,
): number => {
  if (carrierSide === 'long') return loadTypeDimensions.length;

  return loadTypeDimensions.width;
};

const getLoadSizeAlongStationLength = (
  loadTypeDimensions: StationFootprint,
  carrierSide: StationCarrierSide,
): number => {
  if (carrierSide === 'long') return loadTypeDimensions.width;

  return loadTypeDimensions.length;
};

/**
 * Fits the station length to the relevant area axis.
 *
 * The station width stores the visual Short/Long selection while block
 * stacking is active. Angles and stacking intentionally remain unchanged.
 */
export const fitStationSpecToAreaWidth = (
  area: SingularStationArea,
  loadTypeDimensionsById: ReadonlyMap<string, StationFootprint>,
): SingularStationArea => {
  const loadHandlingPoiSpec = area.loadHandlingPoiSpecs[0];
  if (!loadHandlingPoiSpec?.alwaysFitStation) return area;

  const areaFitDimension = getAreaFitDimension(area.constellationSpec.stacking);
  const fittedLength = area.rectangle[areaFitDimension];
  const loadTypeId = area.stationSpec.loadTypeIds[0];
  const loadTypeDimensions = loadTypeDimensionsById.get(loadTypeId);

  let fittedWidth = area.stationSpec.width;

  if (loadTypeDimensions) {
    const carrierSide = getCarrierSideFromStationWidth(area.stationSpec.width, loadTypeDimensions);

    fittedWidth = getStationWidthFromFootprint(loadTypeDimensions, carrierSide);
  }

  fittedWidth = Math.min(fittedWidth, fittedLength);

  if (area.stationSpec.length === fittedLength && area.stationSpec.width === fittedWidth) {
    return area;
  }

  return {
    ...area,
    stationSpec: {
      ...area.stationSpec,
      length: fittedLength,
      width: fittedWidth,
    },
  };
};

export const fitAreaStationSpecToAreaWidth = (
  area: Area,
  loadTypeDimensionsById: ReadonlyMap<string, StationFootprint>,
): Area => {
  if (area.areaType !== 'SINGULAR') return area;

  return fitStationSpecToAreaWidth(area, loadTypeDimensionsById);
};

export const fitAreaStationSpecsToAreaWidth = (
  areas: Areas,
  loadTypeDimensionsById: ReadonlyMap<string, StationFootprint>,
): Areas => {
  const singularStationAreas = areas.singularStationAreas.map((area) =>
    fitStationSpecToAreaWidth(area, loadTypeDimensionsById),
  );

  const hasChanged = singularStationAreas.some(
    (area, index) => area !== areas.singularStationAreas[index],
  );

  if (!hasChanged) return areas;

  return {
    ...areas,
    singularStationAreas,
  };
};

export const getSingularStationCountPerLane = (
  area: SingularStationArea,
  loadTypeDimensionsById: ReadonlyMap<string, StationFootprint>,
): number | undefined => {
  const loadTypeId = area.stationSpec.loadTypeIds[0];
  const loadTypeDimensions = loadTypeDimensionsById.get(loadTypeId);
  if (!loadTypeDimensions) return undefined;

  let carrierSide = getCarrierSideFromPoiAngle(area.loadHandlingPoiSpecs[0]?.angle ?? 0);

  if (area.loadHandlingPoiSpecs[0]?.alwaysFitStation) {
    carrierSide = getCarrierSideFromStationWidth(area.stationSpec.width, loadTypeDimensions);
  }

  const stationSize = area.stationSpec.length;
  const loadSize = getLoadSizeAlongStationLength(loadTypeDimensions, carrierSide);

  if (stationSize <= 0 || loadSize <= 0) return undefined;

  const stationCount = Math.floor(stationSize / loadSize + CAPACITY_EPSILON);
  if (!Number.isFinite(stationCount)) return undefined;

  return Math.max(MINIMUM_STATIONS_PER_LANE, stationCount);
};
